import React from 'react';
import { Card } from '@/components/ui/card';
import { ExternalLink } from 'lucide-react';
import type { LinkPreviewData } from '@/lib/link-preview/api';
import { RichEmbed } from './rich-embed';

interface LinkPreviewCardProps extends LinkPreviewData {
  className?: string;
}

export function LinkPreviewCard({
  url,
  title,
  description,
  image,
  siteName,
  embedHtml,
  embedType,
  aspectRatio,
  embedHeight,
  isEmbeddable,
  provider,
  className = ''
}: LinkPreviewCardProps) {
  const debug = import.meta.env.DEV;
  if (debug) {
    console.log('🎴 [LinkPreviewCard] Received props:', {
      url,
      title,
      siteName,
      isEmbeddable,
      embedType,
      hasEmbedHtml: !!embedHtml,
      embedHtmlLength: embedHtml?.length,
      provider
    });
  }

  // If we have embeddable content, render the rich embed
  if (isEmbeddable && embedHtml) {
    if (debug) {
      console.log('🎵 [LinkPreviewCard] Rendering RichEmbed for:', siteName);
    }
    return (
      <RichEmbed
        embedHtml={embedHtml}
        embedType={embedType}
        aspectRatio={aspectRatio}
        embedHeight={embedHeight}
        title={title}
        siteName={siteName}
        url={url}
        provider={provider}
        className={className}
      />
    );
  }

  if (debug) {
    console.log('📄 [LinkPreviewCard] Rendering card (no embed):', { isEmbeddable, hasEmbedHtml: !!embedHtml });
  }

  // If title is just the URL, extract a better title from the URL
  const displayTitle = title === url || !title ? 
    url.replace(/^https?:\/\/(www\.)?/, '').split('/')[0] : 
    title;

  return (
    <a 
      href={url} 
      target="_blank" 
      rel="noopener noreferrer"
      className={`block no-underline ${className}`}
    >
      <Card className="overflow-hidden hover:shadow-md transition-all duration-200 border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-purple-50 hover:shadow-xl hover:border-blue-300">
        <div className="flex min-h-[120px]">
          {image && (
            <div className="w-1/3 max-w-[200px] flex-shrink-0">
              <img
                src={image}
                alt={displayTitle}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          )}
          {/* Show icon if no image */}
          {!image && (
            <div className="w-24 flex-shrink-0 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <ExternalLink className="w-10 h-10 text-white" />
            </div>
          )}
          <div className="flex-1 p-4 flex flex-col justify-between">
            <div>
              <h3 className="font-bold text-gray-900 line-clamp-2 text-base lg:text-lg mb-2">
                {displayTitle}
              </h3>
              {description && description !== title && (
                <p className="text-sm text-gray-700 mt-1 line-clamp-3 leading-relaxed">
                  {description}
                </p>
              )}
              {!description && url && (
                <p className="text-xs text-gray-600 mt-1 line-clamp-2 break-all">
                  {url}
                </p>
              )}
            </div>
            <div className="flex items-center justify-between mt-3">
              {siteName && (
                <p className="text-xs text-blue-600 font-bold uppercase tracking-wider bg-blue-100 px-2 py-1 rounded">
                  {siteName}
                </p>
              )}
              <ExternalLink className="w-4 h-4 text-blue-600 ml-auto" />
            </div>
          </div>
        </div>
      </Card>
    </a>
  );
}